import { EPageDynamicElementType } from '@hts/shared/enums';
import { useIsMobile } from '@hts/shared/libs';
import {
  ICardGallerySectionEntryItem as ICardGallerySectionItem,
  ICategoryGallerySectionItem,
} from '@hts/shared/types';
import { HtsButtonIcon, HtsTypography, HtslIcon } from '@hts/shared/ui';
import { memo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import Slider, { Settings } from 'react-slick';
import styles from '../styles/GalleryCardSection.module.scss';
import { CardGalleryItem } from './CardGalleryItem';
import { CategoryGalleryItem } from './CategoryGalleryItem';

interface IBasedProps {
  label: string;
  buttonLinkTo: string | null;
  buttonText: string | null;
}

interface ICardGalleryProps extends IBasedProps {
  type: EPageDynamicElementType.CARD_GALLERY;
  cards: ICardGallerySectionItem[];
}

interface ICategoryGalleryProps extends IBasedProps {
  type: EPageDynamicElementType.CATEGORY_GALLERY;
  cards: ICategoryGallerySectionItem[];
}

type IGalleryCardsSectionProps = ICardGalleryProps | ICategoryGalleryProps;

export const GalleryCardsSection: React.FC<IGalleryCardsSectionProps> = memo(
  ({ cards, label, buttonLinkTo, buttonText, type }) => {
    const sliderRef = useRef<Slider>(null);

    const isMobile = useIsMobile();

    const sliderSettings: Settings = {
      infinite: false,
      arrows: false,
      centerMode: false,
      swipeToSlide: true,
      variableWidth: true,
      centerPadding: '-100px',
    };

    const handlePrev = useCallback(() => {
      sliderRef.current?.slickPrev();
    }, []);

    const handleNext = useCallback(() => {
      sliderRef.current?.slickNext();
    }, []);

    const renderCardGallery = useCallback(
      (
        { id, cardId, fileUrl, fileUrlThumbnail, cardName, cardPrice }: ICardGallerySectionItem,
        index: number
      ) => (
        <CardGalleryItem
          key={`${id}-${index}`}
          cardId={cardId}
          price={cardPrice}
          src={fileUrlThumbnail || fileUrl || ''}
          title={cardName}
          name={cardName}
        />
      ),
      []
    );

    const renderCategoryGallery = useCallback(
      (
        {
          id,
          categoryId,
          fileUrl,
          fileUrlThumbnail,
          categoryName,
          categorySlug,
        }: ICategoryGallerySectionItem,
        index: number
      ) => (
        <CategoryGalleryItem
          key={`${id}-${index}`}
          cardId={categoryId}
          src={fileUrlThumbnail || fileUrl || ''}
          name={categoryName}
          slug={categorySlug}
        />
      ),
      []
    );

    return (
      <div
        className={styles.container}
        style={{ gap: type === EPageDynamicElementType.CARD_GALLERY ? 41 : 19 }}
      >
        <div className={styles.labelContainer}>
          <HtsTypography variant="h1">{label}</HtsTypography>

          {buttonText && (
            <Link to={buttonLinkTo || ''} className={styles.seeAllLink}>
              <HtsTypography fontWeight="500" variant={isMobile ? 'subtitle' : 'body1'}>
                {buttonText}
              </HtsTypography>

              <HtslIcon icon="arrow-right" />
            </Link>
          )}
        </div>

        <div className={styles.sliderContainer}>
          <HtsButtonIcon className={styles.arrowLeftButton} onClick={handlePrev}>
            <HtslIcon icon="keyboard-arrow-left" />
          </HtsButtonIcon>

          <Slider {...sliderSettings} ref={sliderRef}>
            {type === EPageDynamicElementType.CARD_GALLERY
              ? cards.map(renderCardGallery)
              : cards.map(renderCategoryGallery)}
          </Slider>

          <HtsButtonIcon className={styles.arrowRightButton} onClick={handleNext}>
            <HtslIcon icon="keyboard-arrow-right" />
          </HtsButtonIcon>
        </div>
      </div>
    );
  }
);
